import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import ThemePickerModal from './ThemePickerModal';

// Full CSS-variable palette payload. This is the "simple color mode" schema —
// note this does NOT currently match what api/theme/settings/update/{id}
// validates on the backend (that endpoint's Request/DTO only knows about
// logo/preloader/typography.primary_color/typography.body_font/etc — see the
// separate ThemeAdvancedModal / ThemeUploadGrid, which target that real
// schema). Backend needs a corresponding change to accept this shape before
// saving here will actually persist; see notes at bottom of file.
const DEFAULT_SETTINGS = {
  colors: {
    '--color-primary': '#F21E26',
    '--light-primary': '#FF666B',
    '--color-secondary': '#B91C1C',
    '--color-tertiary': '#FFE4E6',
    '--color-white': '#FFFFFF',
    '--color-dark': '#18181B',
    '--color-black': '#000000',
    '--color-light': '#E5E7EB',
    '--color-lighter': '#F8FAFC',
    '--color-lightest': '#F1F5F9',
    '--color-heading': '#111827',
    '--color-body': '#374151',
    '--color-border-light': '#E5E7EB',
    '--color-border-dark': '#3F3F46',
    '--color-gray': '#9CA3AF',
    '--color-midgray': '#6B7280',
    '--color-extra01': '#9CA3AF',
    '--color-chart01': '#F21E26',
    '--color-chart02': '#FB7185',
    '--color-chart03': '#7F1D1D',
    '--color-success': '#16A34A',
    '--color-danger': '#DC2626',
    '--color-warning': '#F59E0B',
    '--color-info': '#0EA5E9',
    '--color-facebook': '#3B5997',
    '--color-twitter': '#1BA1F2',
    '--color-youtube': '#ED4141',
    '--color-linkedin': '#0077B5',
    '--color-pinterest': '#E60022',
    '--color-instagram': '#C231A1',
    '--color-vimeo': '#00ADEF',
    '--color-twitch': '#6441A3',
    '--color-discord': '#7289da'
  },
  typography: {
    '--font-primary': "'Inter', system-ui, -apple-system, sans-serif",
    '--font-secondary': "'Inter', system-ui, -apple-system, sans-serif",
    '--font-size-b1': '16px',
    '--font-size-b2': '14px',
    '--font-size-b3': '12px',
    '--h1': '46px',
    '--h2': '30px',
    '--h3': '28px',
    '--h4': '24px',
    '--h5': '20px',
    '--h6': '18px'
  },
  layout: {
    '--radius': '6px',
    '--radius-big': '16px',
    '--radius-small': '6px',
    '--border-width': '2px',
    '--border-thin': '1px',
    '--transition': '0.3s'
  }
};

const TAB_CATEGORIES = [
  { id: 'brand', label: 'Brand & Base Colors' },
  { id: 'surfaces', label: 'Surfaces & Borders' },
  { id: 'status', label: 'Status & Charts' },
  { id: 'socials', label: 'Social Icons' },
  { id: 'typography', label: 'Typography & Layout' },
];

const colorGroups: Record<string, string[]> = {
  brand: ['--color-primary', '--light-primary', '--color-secondary', '--color-tertiary', '--color-white', '--color-dark', '--color-black'],
  surfaces: ['--color-light', '--color-lighter', '--color-lightest', '--color-heading', '--color-body', '--color-border-light', '--color-border-dark', '--color-gray', '--color-midgray', '--color-extra01'],
  status: ['--color-success', '--color-danger', '--color-warning', '--color-info', '--color-chart01', '--color-chart02', '--color-chart03'],
  socials: ['--color-facebook', '--color-twitter', '--color-youtube', '--color-linkedin', '--color-pinterest', '--color-instagram', '--color-vimeo', '--color-twitch', '--color-discord']
};

// ---------------------------------------------------------------------------
// BACKEND CHANGES STILL NEEDED (see message below for exact request):
//
// 1. api/theme/settings/update/{id} — its Request/DTO currently only accepts
//    { logo, preloader, typography: { primary_color, secondary_color,
//    body_font, heading_font }, custom_code }. It needs to also accept this
//    page's shape: { colors: Record<string,string>, typography:
//    Record<string,string>, layout: Record<string,string> } — either as new
//    optional top-level keys, or a dedicated field like `css_variables`.
//    Until that's added, saveCurrentThemeColors() below will 422.
//
// 2. api/theme/create is missing from the generated SDK client entirely
//    (confirmed: no postApiThemeCreate/etc found). Its FormRequest
//    (CreateThemeRequest) already accepts an arbitrary JSON `settings`
//    string, so the backend side is fine — this just needs OpenAPI
//    annotations added to CreateThemeAction (or equivalent) so the client
//    generator picks it up, then a client regen.
// ---------------------------------------------------------------------------

async function saveCurrentThemeColors(themeId: number | string, settings: any) {
  return client.postApiThemeColorUpdateId({
    id: Number(themeId),
    postApiThemeColorUpdateIdRequest: settings
  });
}

async function deleteColorTheme(themeId: number | string) {
  return client.deleteApiThemeColorId({ id: Number(themeId) });
}

export default function ColorGrid() {
  const [themes, setThemes] = useState<any[]>([]);
  const [currentTheme, setCurrentTheme] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState<'pick' | 'create'>('pick');
  const [activeTab, setActiveTab] = useState('brand');
  const [settings, setSettings] = useState<any>(DEFAULT_SETTINGS);

  const fetchThemes = async () => {
    setLoading(true);
    try {
      const [themesRes, activeRes] = await Promise.all([
        client.getApiThemes() as any,
        client.getApiThemeActive() as any
      ]);

      const themesData = themesRes.data?.data?.data || themesRes.data?.data || themesRes.data || [];
      setThemes(Array.isArray(themesData) ? themesData : []);

      const active = activeRes.data?.data || activeRes.data || null;
      setCurrentTheme(active);
    } catch (err) {
      console.error('Failed to fetch theme data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThemes();
  }, []);

  useEffect(() => {
    if (!currentTheme) {
      setSettings(DEFAULT_SETTINGS);
      return;
    }
    try {
      const parsed = typeof currentTheme.settings === 'string'
        ? JSON.parse(currentTheme.settings)
        : (currentTheme.settings || {});
      setSettings({
        colors: { ...DEFAULT_SETTINGS.colors, ...(parsed.colors || {}) },
        typography: { ...DEFAULT_SETTINGS.typography, ...(parsed.typography || {}) },
        layout: { ...DEFAULT_SETTINGS.layout, ...(parsed.layout || {}) }
      });
    } catch {
      setSettings(DEFAULT_SETTINGS);
    }
  }, [currentTheme]);

  const handleColorChange = (key: string, value: string) => {
    setSettings((prev: any) => ({ ...prev, colors: { ...prev.colors, [key]: value } }));
  };

  const handleTextChange = (category: 'typography' | 'layout', key: string, value: string) => {
    setSettings((prev: any) => ({ ...prev, [category]: { ...prev[category], [key]: value } }));
  };

  const handleSaveColors = async () => {
    if (!currentTheme) return;
    setIsSaving(true);
    try {
      await saveCurrentThemeColors(currentTheme.id, settings);
      Swal.fire({
        title: 'Settings Saved',
        text: 'Theme configuration has been updated.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err: any) {
      console.error('Failed to save palette:', err);
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.message || 'Failed to save color palette.',
        icon: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTheme = async (theme: any) => {
    try {
      await deleteColorTheme(theme.id);
      setThemes((prev) => prev.filter((t) => t.id !== theme.id));
      Swal.fire({
        title: 'Theme Deleted',
        text: `${theme.name} has been removed.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err: any) {
      console.error('Failed to delete theme:', err);
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.message || 'Failed to delete theme.',
        icon: 'error'
      });
    }
  };

  const handlePickTheme = async (theme: any) => {
    try {
      await client.postApiThemeActivateId({ id: theme.id }); 
      
      setCurrentTheme(theme);
      setPickerOpen(false);
      
      Swal.fire({
        title: 'Theme Activated',
        text: `${theme.name} is now live!`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
      
    } catch (err: any) {
      console.error('Failed to activate theme:', err);
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.message || 'Could not activate theme in the database.',
        icon: 'error'
      });
    }
  };

  const handleCreateNewTheme = async (name: string) => {
    if (typeof (client as any).postApiThemeCreate !== 'function') {
      Swal.fire({
        title: 'Not Available Yet',
        text: 'Theme creation isn\u2019t wired up on the frontend yet \u2014 api/theme/create needs to be added to the generated API client first.',
        icon: 'info'
      });
      setPickerOpen(false);
      return;
    }

    try {
      const res: any = await (client as any).postApiThemeCreate({
        name,
        path: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        settings: JSON.stringify(DEFAULT_SETTINGS),
        is_active: false
      });
      const created = res?.data?.data || res?.data || { id: `new-${Date.now()}`, name, settings: DEFAULT_SETTINGS };
      Swal.fire({
        title: 'Theme Created',
        text: `${name} has been created successfully.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
      await fetchThemes();
      setCurrentTheme(created);
    } catch (err: any) {
      console.error('Failed to create theme:', err);
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.message || 'Failed to create theme.',
        icon: 'error'
      });
    } finally {
      setPickerOpen(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Modify Current Color Palette</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Editing: <span className="font-semibold text-gray-700 dark:text-gray-300">{currentTheme?.name || 'No theme selected'}</span>
              {loading && <span className="ml-2 italic text-gray-400">Syncing...</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setPickerMode('pick'); setPickerOpen(true); }}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-700 dark:text-gray-200 font-semibold rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              View Themes
            </button>
            <button
              onClick={() => { setPickerMode('create'); setPickerOpen(true); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand-500 text-white font-semibold rounded-xl hover:bg-brand-600 transition-all shadow-lg shadow-brand-500/20"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Theme
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 gap-2 overflow-x-auto">
        {TAB_CATEGORIES.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`py-3 px-4 text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="p-6 space-y-6">
        {['brand', 'surfaces', 'status', 'socials'].includes(activeTab) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {colorGroups[activeTab].map(varKey => (
              <div key={varKey} className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700 flex items-center gap-3">
                <input
                  type="color"
                  value={settings.colors[varKey] || '#000000'}
                  onChange={(e) => handleColorChange(varKey, e.target.value)}
                  className="w-10 h-10 rounded-lg cursor-pointer border-none bg-transparent"
                />
                <div className="flex-1 min-w-0">
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate block">
                    {varKey.replace('--color-', '').replace('--', '')}
                  </label>
                  <input
                    type="text"
                    value={settings.colors[varKey] || ''}
                    onChange={(e) => handleColorChange(varKey, e.target.value)}
                    className="w-full text-xs font-mono bg-transparent text-gray-800 dark:text-white border-b border-gray-200 dark:border-gray-700 focus:outline-none focus:border-brand-500 uppercase"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'typography' && (
          <div>
            <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Font Families</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(settings.typography).filter(([k]) => k.includes('font-')).map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">{key}</label>
                  <input
                    type="text"
                    value={value as string}
                    onChange={(e) => handleTextChange('typography', key, e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 focus:outline-none focus:border-brand-500 font-mono"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
        <button
          type="button"
          onClick={() => setSettings(DEFAULT_SETTINGS)}
          className="text-xs font-semibold text-gray-500 hover:text-red-500 transition-colors"
        >
          Reset to Default
        </button>
        <button
          onClick={handleSaveColors}
          disabled={isSaving || !currentTheme}
          className="px-6 py-2.5 text-xs font-bold bg-brand-500 hover:bg-brand-600 text-white rounded-xl shadow-lg shadow-brand-500/20 transition-all disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Palette'}
        </button>
      </div>

      <ThemePickerModal
        themes={themes}
        currentThemeId={currentTheme?.id ?? null}
        isOpen={pickerOpen}
        initialMode={pickerMode}
        onClose={() => setPickerOpen(false)}
        onPick={handlePickTheme}
        onCreateNew={handleCreateNewTheme}
        onDelete={handleDeleteTheme}
      />
    </div>
  );
}